"use client";

import { useMemo, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { SearchIcon } from "@/components/appIcons";
import { CatalogCollapsibleContent } from "@/components/catalog-collapsible-content";
import { Link } from "@/components/link";
import {
  groupCatalogCompaniesByRole,
  normalizeCatalogCreditsCompaniesSearch,
  searchCatalogCreditsCompaniesRecords,
  type CatalogCompanyRelationship,
  type CatalogCompanyRoleGroup,
} from "@/lib/catalog-credits-companies";
import { formatCreditRole } from "@/lib/format-credit-role";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";
import type { Credit } from "@garimpa/lib/musicTypes";

const SEARCH_THRESHOLD = 10;

type CreditGroup = [role: string, credits: Credit[]];

type RoleRowEntry = {
  key: string;
  name: string;
  to: string | null;
};

type RoleRow = {
  entries: RoleRowEntry[];
  key: string;
  label: string;
};

function useCreditRoleLabels(creditGroups: CreditGroup[]) {
  const creditRoleMessages = useTranslations("shared.creditRoles");
  return new Map(
    creditGroups.map(([role]) => {
      const roleKey = role as Parameters<typeof creditRoleMessages>[0];
      const label = creditRoleMessages.has(roleKey)
        ? creditRoleMessages(roleKey)
        : formatCreditRole(role);
      return [role, label] as const;
    }),
  );
}

function useCompanyRoleLabels(companyGroups: CatalogCompanyRoleGroup[]) {
  const companyRoleMessages = useTranslations("shared.releaseCompanyRoles");
  return new Map(
    companyGroups.map((group) => {
      const roleKey = group.role as Parameters<typeof companyRoleMessages>[0];
      const label = companyRoleMessages.has(roleKey)
        ? companyRoleMessages(roleKey)
        : formatCatalogTaxonomyLabel(group.role);
      return [group.role, label] as const;
    }),
  );
}

function companyIdentity(company: CatalogCompanyRelationship) {
  return company.companyId
    ? `id:${company.companyId}`
    : `name:${normalizeCatalogCreditsCompaniesSearch(company.companyName)}`;
}

function pickVisible<T>(visibleKeys: string[], groupsByKey: Map<string, T>): T[] {
  return visibleKeys
    .map((key) => groupsByKey.get(key))
    .filter((group): group is T => group !== undefined);
}

function useCreditsCompaniesModel(
  creditsByRole: Record<string, Credit[]> | null | undefined,
  companies: CatalogCompanyRelationship[] | null | undefined,
  query: string,
) {
  const creditGroups = useMemo<CreditGroup[]>(
    () => Object.entries(creditsByRole ?? {}).filter(([, credits]) => credits.length > 0),
    [creditsByRole],
  );
  const companyGroups = useMemo(() => groupCatalogCompaniesByRole(companies), [companies]);
  const creditLabels = useCreditRoleLabels(creditGroups);
  const companyLabels = useCompanyRoleLabels(companyGroups);
  const records = [
    ...creditGroups.map(([role, credits]) => ({
      key: `credit:${role}`,
      kind: "credit" as const,
      searchText: normalizeCatalogCreditsCompaniesSearch(
        [role, creditLabels.get(role), ...credits.map((credit) => credit.artistName)].join(" "),
      ),
    })),
    ...companyGroups.map((group) => ({
      key: `company:${group.key}`,
      kind: "company" as const,
      searchText: normalizeCatalogCreditsCompaniesSearch(
        [
          group.role,
          companyLabels.get(group.role),
          ...group.companies.map((company) => company.companyName),
        ].join(" "),
      ),
    })),
  ];
  const showSearch = records.length >= SEARCH_THRESHOLD;
  const visibleRecords = searchCatalogCreditsCompaniesRecords(records, showSearch ? query : "");
  const keysOf = (kind: "credit" | "company") =>
    visibleRecords.filter((record) => record.kind === kind).map((record) => record.key);
  const visibleCreditGroups = pickVisible(
    keysOf("credit"),
    new Map(creditGroups.map((group) => [`credit:${group[0]}`, group] as const)),
  );
  const visibleCompanyGroups = pickVisible(
    keysOf("company"),
    new Map(companyGroups.map((group) => [`company:${group.key}`, group] as const)),
  );

  const creditRows = visibleCreditGroups.map<RoleRow>(([role, credits]) => ({
    key: role,
    label: creditLabels.get(role) ?? role,
    entries: credits.map((credit) => ({
      key: `${credit.role}-${credit.artistId}`,
      name: credit.artistName,
      to: `/artist/${credit.artistId}`,
    })),
  }));
  const companyRows = visibleCompanyGroups.map<RoleRow>((group) => ({
    key: group.key,
    label: companyLabels.get(group.role) ?? group.role,
    entries: group.companies.map((company) => ({
      key: `${company.role}-${company.companyId ?? company.companyName}`,
      name: company.companyName,
      to: company.companyId ? `/company/${company.companyId}` : null,
    })),
  }));

  return {
    companyRows,
    creditRows,
    hasActiveQuery: showSearch && normalizeCatalogCreditsCompaniesSearch(query).length > 0,
    hasCompanies: companyGroups.length > 0,
    hasCredits: creditGroups.length > 0,
    noMatches: records.length > 0 && visibleRecords.length === 0,
    showSearch,
    summary: {
      companyCount: new Set(
        visibleCompanyGroups.flatMap((group) => group.companies.map(companyIdentity)),
      ).size,
      creditCount: visibleCreditGroups.reduce((count, [, credits]) => count + credits.length, 0),
      relationshipCount: visibleCompanyGroups.reduce(
        (count, group) => count + group.companies.length,
        0,
      ),
      roleCount: visibleCreditGroups.length,
    },
  };
}

type CreditsCompaniesModel = ReturnType<typeof useCreditsCompaniesModel>;

export function CatalogCreditsCompaniesSection({
  companies,
  creditsByRole,
}: {
  companies: CatalogCompanyRelationship[] | null | undefined;
  creditsByRole: Record<string, Credit[]> | null | undefined;
}) {
  const t = useTranslations("release");
  const [query, setQuery] = useState("");
  const [creditsExpanded, setCreditsExpanded] = useState(false);
  const [companiesExpanded, setCompaniesExpanded] = useState(false);
  const model = useCreditsCompaniesModel(creditsByRole, companies, query);
  const hasAnyGroup = model.hasCredits || model.hasCompanies;

  return (
    <section className="min-w-0" aria-labelledby="catalog-credits-production-title">
      <CreditsCompaniesHeader model={model} query={query} onQueryChange={setQuery} />

      {hasAnyGroup && (
        <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          {model.creditRows.length > 0 && (
            <RoleCard
              contentId="catalog-credits-content"
              expanded={creditsExpanded}
              forceExpanded={model.hasActiveQuery}
              onExpandedChange={setCreditsExpanded}
              rows={model.creditRows}
              showLessLabel={t("showLessCredits")}
              showMoreLabel={t("showMoreCredits")}
              title={t("credits")}
              titleId="catalog-credits-title"
              tone="credits"
            />
          )}
          {model.companyRows.length > 0 && (
            <RoleCard
              contentId="catalog-companies-content"
              expanded={companiesExpanded}
              forceExpanded={model.hasActiveQuery}
              onExpandedChange={setCompaniesExpanded}
              rows={model.companyRows}
              showLessLabel={t("showLessCompanies")}
              showMoreLabel={t("showMoreCompanies")}
              title={t("companiesPlaces")}
              titleId="catalog-companies-title"
              tone="companies"
            />
          )}
        </div>
      )}

      {!hasAnyGroup && <p className="mt-6 text-sm text-muted-foreground">{t("noCredits")}</p>}
      {model.noMatches && (
        <p className="mt-6 border-t border-border pt-4 text-sm text-muted-foreground">
          {t("noCreditsCompaniesMatch")}
        </p>
      )}
    </section>
  );
}

function CreditsCompaniesHeader({
  model,
  onQueryChange,
  query,
}: {
  model: CreditsCompaniesModel;
  onQueryChange: (query: string) => void;
  query: string;
}) {
  const t = useTranslations("release");
  const summaryParts = [
    model.hasCredits ? t("creditsSummary", model.summary) : null,
    model.hasCompanies ? t("companiesSummary", model.summary) : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2
          id="catalog-credits-production-title"
          className="text-2xl font-bold tracking-tight sm:text-[28px]"
        >
          {t("creditsProduction")}
        </h2>
        {summaryParts.length > 0 && (
          <span className="text-sm text-muted-foreground" aria-live="polite">
            {summaryParts.join(" · ")}
          </span>
        )}
      </div>
      {model.showSearch && (
        <label className="relative block w-full min-w-0 sm:max-w-sm">
          <span className="sr-only">{t("creditsCompaniesSearchPlaceholder")}</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.currentTarget.value)}
            placeholder={t("creditsCompaniesSearchPlaceholder")}
            aria-label={t("creditsCompaniesSearchPlaceholder")}
            className="h-11 w-full rounded-lg border border-border/70 bg-background/70 pl-10 pr-3 text-sm font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/60 focus:bg-background"
          />
        </label>
      )}
    </div>
  );
}

function RoleCard({
  contentId,
  expanded,
  forceExpanded,
  onExpandedChange,
  rows,
  showLessLabel,
  showMoreLabel,
  title,
  titleId,
  tone,
}: {
  contentId: string;
  expanded: boolean;
  forceExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  rows: RoleRow[];
  showLessLabel: string;
  showMoreLabel: string;
  title: ReactNode;
  titleId: string;
  tone: "credits" | "companies";
}) {
  return (
    <section
      aria-labelledby={titleId}
      // The collapse fade paints with `bg-background`; point it at the card surface.
      className="min-w-0 rounded-2xl border border-border/60 bg-card px-5 py-5 [--background:var(--card)] sm:px-6"
    >
      <h3
        id={titleId}
        className={cn(
          "mb-2 text-xs font-bold uppercase tracking-wider",
          tone === "credits" ? "text-chart-1" : "text-chart-2",
        )}
      >
        {title}
      </h3>
      <CatalogCollapsibleContent
        contentId={contentId}
        expanded={expanded}
        forceExpanded={forceExpanded}
        onExpandedChange={onExpandedChange}
        showMoreLabel={showMoreLabel}
        showLessLabel={showLessLabel}
      >
        <dl className="flex min-w-0 flex-col">
          {rows.map((row) => (
            <RoleRowItem key={row.key} row={row} />
          ))}
        </dl>
      </CatalogCollapsibleContent>
    </section>
  );
}

function RoleRowItem({ row }: { row: RoleRow }) {
  return (
    <div className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-1 border-b border-border/40 py-2.5 last:border-b-0 sm:grid-cols-[150px_minmax(0,1fr)]">
      <dt className="min-w-0 break-words text-sm text-muted-foreground">{row.label}</dt>
      <dd className="min-w-0">
        <ul className="flex min-w-0 flex-wrap gap-x-1.5 gap-y-0.5 text-sm font-semibold leading-relaxed">
          {row.entries.map((entry) => (
            <li
              key={entry.key}
              className="min-w-0 after:ml-1.5 after:text-muted-foreground after:content-['·'] last:after:content-none"
            >
              <RoleRowEntryName entry={entry} />
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

function RoleRowEntryName({ entry }: { entry: RoleRowEntry }) {
  if (!entry.to) return <span className="break-words">{entry.name}</span>;
  return (
    <Link appearance="text" to={entry.to} className="break-words text-foreground">
      {entry.name}
    </Link>
  );
}
